import { auth } from './firebase';
import { recordRecentError } from './recentErrors';

export const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8080/api/v1';

// Enforce HTTPS in production - block insecure API URLs
if (import.meta.env.PROD && API_URL && !API_URL.startsWith('https://')) {
  throw new Error('SECURITY: API_URL must use HTTPS in production. Refusing to start with insecure configuration.');
}

/**
 * Erro padronizado da API: {success:false, error:{code,message,details?,correlationId}}.
 * `status` 0 indica falha de rede.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: Record<string, unknown>;
  readonly correlationId?: string;

  constructor(params: {
    message: string;
    status: number;
    code?: string;
    details?: Record<string, unknown>;
    correlationId?: string;
  }) {
    super(params.message);
    this.name = 'ApiError';
    this.status = params.status;
    this.code = params.code ?? 'UNKNOWN_ERROR';
    this.details = params.details;
    this.correlationId = params.correlationId;
  }

  /** Campo apontado pelo backend em details.field (erros de validação) */
  get field(): string | undefined {
    const field = this.details?.field;
    return typeof field === 'string' ? field : undefined;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/** Mensagem amigável para qualquer erro, com tratamento dos códigos conhecidos. */
export function getErrorMessage(error: unknown, fallback = 'Ocorreu um erro inesperado. Tente novamente.'): string {
  if (isApiError(error)) {
    if (error.status === 404) return error.message && error.code !== 'UNKNOWN_ERROR' ? error.message : 'Não encontrado.';
    if (error.code === 'CAPTCHA_INVALID') return 'A verificação de segurança falhou ou expirou. Confirme a verificação e tente novamente.';
    return error.message || fallback;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

interface RequestOptions extends RequestInit {
  requireAuth?: boolean;
  /** Token do Google reCAPTCHA, enviado em `x-captcha-token` */
  captchaToken?: string | null;
  /** Usa um token específico (ex.: idToken recém-obtido do Google) */
  bearerToken?: string;
  /** Envia o Bearer se houver sessão, sem exigir (ex.: /feedback) */
  optionalAuth?: boolean;
}

async function buildHeaders(options: RequestOptions, isJson: boolean): Promise<Record<string, string>> {
  const { requireAuth = true, captchaToken, bearerToken, optionalAuth } = options;
  const headers: Record<string, string> = {
    ...(isJson ? { 'Content-Type': 'application/json' } : {}),
    ...(options.headers as Record<string, string> | undefined),
  };

  if (captchaToken) headers['x-captcha-token'] = captchaToken;

  if (bearerToken) {
    headers['Authorization'] = `Bearer ${bearerToken}`;
  } else if (requireAuth) {
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      throw new ApiError({ message: 'Sessão expirada. Faça login novamente.', status: 401, code: 'UNAUTHORIZED' });
    }
    headers['Authorization'] = `Bearer ${token}`;
  } else if (optionalAuth && auth.currentUser) {
    try {
      headers['Authorization'] = `Bearer ${await auth.currentUser.getIdToken()}`;
    } catch {
      // Sem token válido a rota segue como anônima (autenticação é opcional)
    }
  }

  return headers;
}

async function doFetch(endpoint: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(`${API_URL}${endpoint}`, init);
  } catch {
    recordRecentError({ mensagem: `Falha de rede em ${endpoint.split('?')[0]}`, origem: 'api' });
    throw new ApiError({
      message: 'Não foi possível conectar ao servidor. Verifique sua conexão e tente novamente.',
      status: 0,
      code: 'NETWORK_ERROR',
    });
  }
}

/** Último erro HTTP recebido — permite ao toast "Reportar" anexar o correlationId */
let lastApiError: ApiError | null = null;

export function getLastApiError(): ApiError | null {
  return lastApiError;
}

async function toApiError(response: Response, fallbackMessage: string): Promise<ApiError> {
  const correlationId = response.headers.get('x-correlation-id') ?? undefined;
  let body: { error?: { code?: string; message?: string; details?: Record<string, unknown>; correlationId?: string } } | null = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }
  const error = body?.error;
  let message = error?.message ?? fallbackMessage;
  if (response.status === 404 && !error?.message) message = 'Não encontrado.';
  lastApiError = new ApiError({
    message,
    status: response.status,
    code: error?.code,
    details: error?.details,
    correlationId: error?.correlationId ?? correlationId,
  });
  recordRecentError({ mensagem: `${response.status} ${message}`, origem: 'api', correlationId: lastApiError.correlationId });
  return lastApiError;
}

async function request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { requireAuth: _requireAuth, captchaToken: _captcha, bearerToken: _bearer, optionalAuth: _optional, ...fetchOptions } = options;
  const headers = await buildHeaders(options, true);

  const response = await doFetch(endpoint, { ...fetchOptions, headers });

  if (!response.ok) {
    throw await toApiError(response, `Erro ${response.status}. Tente novamente.`);
  }

  if (response.status === 204) return undefined as T;

  let data: Record<string, unknown>;
  try {
    data = await response.json();
  } catch {
    throw new ApiError({ message: 'Resposta inválida do servidor.', status: response.status, code: 'INVALID_RESPONSE' });
  }

  return data.data as T;
}

/** Como `request`, mas devolve também o `meta` (paginação, totais) */
async function requestWithMeta<T, M>(endpoint: string, options: RequestOptions = {}): Promise<{ data: T; meta: M }> {
  const { requireAuth: _requireAuth, captchaToken: _captcha, bearerToken: _bearer, optionalAuth: _optional, ...fetchOptions } = options;
  const headers = await buildHeaders(options, true);
  const response = await doFetch(endpoint, { ...fetchOptions, headers });
  if (!response.ok) {
    throw await toApiError(response, `Erro ${response.status}. Tente novamente.`);
  }
  try {
    const body = (await response.json()) as { data: T; meta: M };
    return { data: body.data, meta: body.meta };
  } catch {
    throw new ApiError({ message: 'Resposta inválida do servidor.', status: response.status, code: 'INVALID_RESPONSE' });
  }
}

export const api = {
  get: <T>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'GET' }),

  getWithMeta: <T, M>(endpoint: string, options?: RequestOptions) =>
    requestWithMeta<T, M>(endpoint, { ...options, method: 'GET' }),

  post: <T>(endpoint: string, body: unknown, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'POST', body: JSON.stringify(body) }),

  put: <T>(endpoint: string, body: unknown, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'PUT', body: JSON.stringify(body) }),

  patch: <T>(endpoint: string, body: unknown, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'PATCH', body: JSON.stringify(body) }),

  delete: <T>(endpoint: string, options?: RequestOptions & { body?: string }) =>
    request<T>(endpoint, { ...options, method: 'DELETE' }),

  /** DELETE com corpo JSON (ex.: confirmação de exclusão de conta) */
  deleteWithBody: <T>(endpoint: string, body: unknown, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'DELETE', body: JSON.stringify(body) }),

  /** Baixa a resposta crua como Blob (ex.: exportação de dados) */
  getBlob: async (endpoint: string, options: RequestOptions = {}): Promise<Blob> => {
    const headers = await buildHeaders(options, false);
    const response = await doFetch(endpoint, { method: 'GET', headers });
    if (!response.ok) throw await toApiError(response, 'Não foi possível baixar o arquivo.');
    return response.blob();
  },

  // Upload de arquivos (FormData)
  upload: async <T>(endpoint: string, formData: FormData): Promise<T> => {
    // Content-Type não é definido: o browser adiciona o boundary do multipart
    const headers = await buildHeaders({ requireAuth: true }, false);
    const response = await doFetch(endpoint, { method: 'POST', headers, body: formData });

    if (!response.ok) throw await toApiError(response, 'Erro ao fazer upload.');

    let data: Record<string, unknown>;
    try {
      data = await response.json();
    } catch {
      throw new ApiError({ message: 'Resposta inválida do servidor.', status: response.status, code: 'INVALID_RESPONSE' });
    }
    return data.data as T;
  },
};
